import React, { useEffect, useState, useCallback } from 'react';
import update from 'immutability-helper';
import DNDScrollZone from 'components/shared/DNDScrollZone';
import { Icon, ButtonGroup, Button, Card, Spinner } from 'ui';
import TextArea from 'components/shared/debouncedTextArea';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import trackEvent from 'misc/trackEvent';
import RoundedInput from 'components/Practice/Interaction/RoundedInput';
import { CustomDragLayer } from 'components/Practice/Interaction/CustomDragLayer';
import Interaction from './Interaction';

const ConversationScenarioEditor = React.forwardRef((props, ref) => {
  const {
    scenarioId,
    scenario,
    loading,
    saved,
    updatePrompt,
    updateDescription,
    updateTitle,
  } = props;
  const {
    title,
    description,
    prompt,
    conversation: initialConversation = {},
    containerVisibilityGroups,
  } = scenario;

  const { helpLink, customerNameMetadatumId } = initialConversation;
  const [customerName, setCustomerName] = useState(
    initialConversation.customerName,
  );
  const [interactions, setInteractions] = useState(
    initialConversation.interactions || [],
  );
  const [shouldCollapseInteractions, setShouldCollapseInteractions] =
    useState(false);
  const [showWarning, setShowWarning] = useState(
    containerVisibilityGroups.length > 0,
  );
  const orderedInteractions = interactions.sort(
    (a, b) => a.position - b.position,
  );

  const newInteraction = {
    position: interactions.length + 1 || 1,
  };

  const addInteraction = () => {
    const route =
      routes.ON_DEMAND_PRACTICE.CREATOR.CONVERSATIONS.QUESTIONS.INTERACTIONS.CREATE(
        scenario.question.id,
      );
    request.post(route, newInteraction).then(({ interaction }) => {
      setInteractions([...interactions, interaction]);
    });
  };
  useEffect(() => {
    if (interactions.length === 0) {
      addInteraction();
    }
    //eslint-disable-next-line
  }, []);

  const updateCustomerName = name => {
    const route = routes.QUESTIONS.METADATUM(
      scenario.question.id,
      customerNameMetadatumId,
    );
    request.put(route, { value: name });
  };

  const updateInteraction = updatedInteraction => {
    const newInteractions = interactions.map(int =>
      int.id === updatedInteraction.id ? updatedInteraction : int,
    );
    setInteractions(newInteractions);
    const route =
      routes.ON_DEMAND_PRACTICE.CREATOR.CONVERSATIONS.QUESTIONS.INTERACTIONS.UPDATE(
        scenario.question.id,
        updatedInteraction.id,
      );
    request.put(route, updatedInteraction);
  };

  const reorderInteractions = newIndex => {
    const updatedInteraction = interactions[newIndex];

    const route =
      routes.ON_DEMAND_PRACTICE.CREATOR.CONVERSATIONS.QUESTIONS.INTERACTIONS.UPDATE(
        scenario.question.id,
        updatedInteraction.id,
      );

    request.put(route, updatedInteraction);
  };

  const reorderInteractionsInterface = (newIndex, oldIndex) => {
    const updatedInteractions = interactions.map((int, index) => {
      if (index === oldIndex) {
        const updatedInteraction = int;
        updatedInteraction.position = newIndex + 1;
        return updatedInteraction;
      }
      if (index > oldIndex && index <= newIndex) {
        const updatedInteraction = int;
        updatedInteraction.position -= 1;
        return updatedInteraction;
      }
      if ((index < oldIndex && index >= newIndex) || index === newIndex) {
        const updatedInteraction = int;
        updatedInteraction.position += 1;
        return updatedInteraction;
      }
      return int;
    });
    const sortedInteractions = updatedInteractions.sort(
      (a, b) => a.position - b.position,
    );
    setInteractions(sortedInteractions);
  };

  const deleteInteraction = interactionToDelete => {
    const updatedInteractions = interactions
      .filter(int => int.id !== interactionToDelete.id)
      .map(int => {
        if (int.position < interactionToDelete.position) return int;
        if (int.position > interactionToDelete.position) {
          const newPosition = int.position - 1;
          return { ...int, position: newPosition };
        }
        return int;
      });
    setInteractions(updatedInteractions);

    const route =
      routes.ON_DEMAND_PRACTICE.CREATOR.CONVERSATIONS.QUESTIONS.INTERACTIONS.DELETE(
        scenario.question.id,
        interactionToDelete.id,
      );

    request.delete(route);
  };

  const moveInteraction = useCallback(
    (dragIndex, hoverIndex) => {
      setInteractions(prevCards =>
        update(interactions, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, interactions[dragIndex]],
          ],
        }).map((interaction, index) => ({
          ...interaction,
          position: index + 1,
        })),
      );
    },
    [interactions],
  );

  const showErrorMessage =
    !customerName ||
    !prompt ||
    !!interactions.some(
      int => !int.subject || int.responses.some(res => !res.text),
    );
  return (
    <>
      <DNDScrollZone willReceiveDraggable={shouldCollapseInteractions} />
      {showWarning && showErrorMessage && (
        <div className="NotificationContainer NotificationContainer--warning bdrs-2 mb-16 d-f ai-c jc-sb c-black">
          <span>
            <Icon name="warning_sign" customClass="mr-8" />
            <span className="fw-b mr-4">
              {t(
                'practice.on_demand_practice.editor.conversations.incomplete_conversation_error_start',
              )}
            </span>
            {t(
              'practice.on_demand_practice.editor.conversations.incomplete_conversation_error_end',
            )}
          </span>
          <Button type="button" onClick={() => setShowWarning(false)}>
            {t('common.dismiss')}
          </Button>
        </div>
      )}
      <div className="ScenarioEditor ff-ss p-24 maxw-layout OnDemandPracticeConversation">
        {loading && <Spinner />}
        {!loading && (
          <>
            <a
              href={routes.ON_DEMAND_PRACTICE.CREATOR.SHOW(scenarioId)}
              className="c-brand-blue td-u"
            >
              {t(
                'practice.on_demand_practice.editor.conversations.conversation_overview',
              )}
            </a>
            <div className="ScenarioEditor-header d-f jc-sb ai-fe mb-16 mt-32">
              <span>
                <h1 className="ff-ss fw-n mb-12">
                  {t(
                    'practice.on_demand_practice.editor.conversations.conversation_builder',
                  )}
                </h1>
                {helpLink && (
                  <p className="ff-ss fw-n">
                    {t(
                      'practice.on_demand_practice.editor.conversations.conversation_start_link_start',
                    )}
                    <a
                      href={helpLink}
                      className="c-brand-blue m-4 td-u"
                      onClick={() => {
                        trackEvent('practice/example_conversation_clicked');
                      }}
                    >
                      {t(
                        'practice.on_demand_practice.editor.conversations.conversation_start_link_middle',
                      )}
                    </a>
                    {t(
                      'practice.on_demand_practice.editor.conversations.conversation_start_link_end',
                    )}
                  </p>
                )}
              </span>
              <ButtonGroup>
                <div
                  role="alert"
                  aria-hidden={saved}
                  className={`ScenarioEditor-saved ${
                    saved && 'ScenarioEditor-saved--shown'
                  }`}
                >
                  {t('common.saved')}
                </div>
                <Button
                  url={routes.ON_DEMAND_PRACTICE.PREVIEW.SHOW(scenarioId)}
                  target="_blank"
                >
                  {t('common.preview')}
                </Button>
              </ButtonGroup>
            </div>
            <Card>
              <Card.Content padding="large">
                <label htmlFor="container-title" className="v-hide">
                  {t('practice.on_demand_practice.editor.scenario_title')}
                </label>
                <TextArea
                  id="container-title"
                  value={title}
                  className="ScenarioEditor-title fz-24 Animated mb-8"
                  onChange={updateTitle}
                  placeholder={t(
                    'practice.on_demand_practice.editor.scenario_title',
                  )}
                  singleLine
                />
                <label htmlFor="container-description" className="v-hide">
                  {t('practice.on_demand_practice.editor.scenario_description')}
                </label>
                <TextArea
                  id="container-description"
                  value={description}
                  className="ScenarioEditor-description fz-16 Animated mb-16"
                  onChange={updateDescription}
                  placeholder={t(
                    'practice.on_demand_practice.editor.scenario_description',
                  )}
                  singleLine
                />
                <span className="d-f">
                  <RoundedInput
                    labelText={`${t(
                      'practice.on_demand_practice.editor.conversations.customer_name',
                    )}*`}
                    id="conversation-customer-name"
                    placeholderText={t(
                      'practice.on_demand_practice.editor.conversations.customer_name_placeholder',
                    )}
                    value={customerName}
                    onChange={name => {
                      setCustomerName(name);
                      updateCustomerName(name);
                    }}
                    singleLine
                    hasError={!customerName}
                  />
                  <span className="d-f fg-1 jc-e fz-12 mt-24">{`*${t(
                    'practice.on_demand_practice.editor.conversations.required',
                  )}`}</span>
                </span>
                <RoundedInput
                  labelText={`${t(
                    'practice.on_demand_practice.editor.conversations.conversation_prompt',
                  )}*`}
                  id="conversation-prompt"
                  placeholderText={t(
                    'practice.on_demand_practice.editor.conversations.conversation_prompt_placeholder',
                  )}
                  value={prompt}
                  onChange={updatePrompt}
                  singleLine
                  wide
                  hasError={!prompt}
                />
                <div className="mt-32">
                  {orderedInteractions.map((interaction, index) => {
                    return (
                      <Interaction
                        index={index}
                        interaction={interaction}
                        key={interaction.id}
                        updateInteraction={updateInteraction}
                        deleteInteraction={deleteInteraction}
                        showDelete={interactions.length > 1}
                        moveInteraction={moveInteraction}
                        reorderInteractions={reorderInteractions}
                        reorderInteractionsInterface={
                          reorderInteractionsInterface
                        }
                        setShouldCollapse={val =>
                          setShouldCollapseInteractions(val)
                        }
                        expanded={
                          index === orderedInteractions.length - 1 &&
                          !shouldCollapseInteractions
                        }
                        shouldCollapse={shouldCollapseInteractions}
                        question={scenario?.question}
                      />
                    );
                  })}
                  <CustomDragLayer />
                  <div className="border-1 border-gray-light border-solid border-top-0">
                    <button
                      type="button"
                      className="border-0 c-brand-blue bg-white td-u f-o-n haf-c-lhci mt-24 ml-16 mb-24"
                      onClick={() => addInteraction()}
                    >
                      <Icon name="add" className="mr-4" />
                      {t(
                        'practice.on_demand_practice.editor.conversations.add_additional_interaction',
                      )}
                    </button>
                  </div>
                </div>
                {showErrorMessage && (
                  <p className="c-red fs-i fz-16 mt-8">
                    {t(
                      'practice.on_demand_practice.editor.conversations.incomplete_error_message',
                    )}
                  </p>
                )}
              </Card.Content>
            </Card>
          </>
        )}
      </div>
    </>
  );
});

export default ConversationScenarioEditor;
